import { TimeFormatOptions } from '@griply/types';
import { applyMsSinceMidnight, toMsSinceMidnight } from '@griply/utils';
import { IconButton, InputAdornment } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  TimeField as MuiTimeField,
  TimeFieldProps as MuiTimeFieldProps,
} from '@mui/x-date-pickers/TimeField';
import React from 'react';
import { X } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';

export type TimeFieldProps = Omit<MuiTimeFieldProps, 'value' | 'onChange'> & {
  value: number | null;
  onChange: (value: number | null) => void;
  timeFormat?: TimeFormatOptions;
  canClearEntry?: boolean;
};

export const TimeField: React.FC<TimeFieldProps> = ({
  value,
  onChange,
  timeFormat = TimeFormatOptions.Military,
  canClearEntry,
  ...props
}) => {
  const handleClearClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleChange = (date: Date | null) => {
    if (!date) {
      onChange(null);
      return;
    }

    onChange(toMsSinceMidnight(date));
  };

  // Convert our number format (ms from start of day) to Date object for MUI
  const dateValue = React.useMemo(() => {
    if (value === null) {
      return null;
    }
    return applyMsSinceMidnight(new Date(), value);
  }, [value]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MuiTimeField
        value={dateValue}
        onChange={handleChange}
        format={timeFormat === TimeFormatOptions.Military ? 'HH:mm' : 'hh:mm a'}
        ampm={timeFormat !== TimeFormatOptions.Military}
        {...props}
        slotProps={{
          textField: {
            InputProps: {
              endAdornment: canClearEntry && value !== null && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearClick}
                    edge="end"
                    size="small"
                    sx={{ color: 'grey.600' }}
                  >
                    <Icon icon={X} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};
